import { HTTPError } from "ky";

import i18n from "@/i18n";
import { type ApiErrorBody, readApiError } from "@/shared/api/client";

const STATUS_KEYS: Record<number, string> = {
	400: "errors.http.400",
	401: "errors.http.401",
	403: "errors.http.403",
	404: "errors.http.404",
	409: "errors.http.409",
	422: "errors.http.422",
	429: "errors.http.429",
	500: "errors.http.500",
	502: "errors.http.500",
	503: "errors.http.500",
};

function translate(key: string): string {
	const value = i18n.t(key);
	return value === key ? "" : value;
}

function messageForCode(code: string | undefined): string {
	if (!code) {
		return "";
	}
	return translate(`errors.codes.${code}`);
}

function messageForStatus(status: number | undefined): string {
	if (!status) {
		return "";
	}
	const key = STATUS_KEYS[status] ?? (status >= 500 ? "errors.http.500" : "");
	return key ? translate(key) : "";
}

export function messageForApiBody(body: ApiErrorBody, status?: number): string {
	return (
		messageForCode(body.code) ||
		messageForStatus(body.statusCode ?? status) ||
		i18n.t("errors.generic")
	);
}

/** Sync fallback for query alerts — maps HTTP status, never raw ky/English text. */
export function describeApiErrorSync(error: unknown): string {
	if (error instanceof HTTPError) {
		return messageForStatus(error.response.status) || i18n.t("errors.generic");
	}
	if (error instanceof TypeError) {
		return i18n.t("errors.network");
	}
	return i18n.t("errors.generic");
}

export async function describeApiError(error: unknown): Promise<string> {
	if (error instanceof HTTPError) {
		const body = await readApiError(error.response);
		return messageForApiBody(body, error.response.status);
	}
	if (error instanceof TypeError) {
		return i18n.t("errors.network");
	}
	return i18n.t("errors.generic");
}
